import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GPSSelectionHeader from '../components/GPS/GPSSelectionHeader';
import GPSTabs, { getGPSTabs } from '../components/GPS/GPSTabs';
import GPSCustomPage from '../components/GPS/GPSCustomPage';
import ViewerSidebar from '../components/ui/ViewerSidebar';
import CardSelect from '../components/ui/CardSelect';
import { GPSProvider, useGPS } from '../contexts/GPSContext';
import useAnalytics from '../hooks/useAnalytics';
import useTabParam from '../hooks/useTabParam.js';
import { safeParseDate, toDateParam } from '../utils/dateUtils.js';
import baseFetchWithAuth from '../utils/fetchWithAuth';

function GPSViewerContent({ currentUser }) {
  const gps = useGPS();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = getGPSTabs(currentUser);
  const projectKey = gps.selectedProjet?.value;

  // The custom pages are config, not code: the list drives the sidebar entries (one
  // per page) and the sub-menus (one per tab). Loaded here rather than inside the
  // page because the menu must be there on first paint. `null` = still loading.
  const [customPages, setCustomPages] = useState(null);

  useEffect(() => {
    if (!projectKey) { setCustomPages(null); return; }
    let stale = false;
    baseFetchWithAuth(`/api/dwh/gps/custom-pages/${encodeURIComponent(projectKey)}`)
      .then((pages) => { if (!stale) setCustomPages(Array.isArray(pages) ? pages : []); })
      .catch(() => { if (!stale) setCustomPages([]); });
    return () => { stale = true; };
  }, [projectKey]);

  // Until the list arrives, a deep link to a custom page resolves to "Général" for a
  // moment; useTabParam never rewrites the URL on an unknown id, so the link then
  // settles on the page. The alternative — trusting the URL while loading — would
  // also trust ?tab=bogus, which must fall back to "Général".
  const tabIds = useMemo(() => [
    ...tabs.map((t) => t.id),
    ...(customPages || []).map((p) => p.id),
  ], [tabs, customPages]);
  const [activeTab, setActiveTab] = useTabParam(tabIds, 'general');

  const isCustomPage = !tabs.some((t) => t.id === activeTab);
  const activePage = (customPages || []).find((p) => p.id === activeTab) || null;

  const subItemsOf = (page) => (page?.tabs || []).map((tab) => ({ id: tab.id, label: tab.label }));
  const activeSubItems = subItemsOf(activePage);

  // Sub-menu selection lives in the URL like the tab does, so a sub-menu is linkable.
  const urlSub = searchParams.get('sub');
  const activeSub = activeSubItems.some((s) => s.id === urlSub) ? urlSub : activeSubItems[0]?.id ?? null;

  const setActiveSub = useCallback((subId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    next.set('sub', subId);
    setSearchParams(next);
  }, [searchParams, setSearchParams, activeTab]);

  const buildSubHref = useCallback((subId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    next.set('sub', subId);
    const qs = next.toString();
    return qs ? `?${qs}` : '';
  }, [searchParams, activeTab]);

  const jailRef = useRef(searchParams.has('jail'));
  const jail = jailRef.current;

  // Gates the date→URL write until the initial URL→context read has run. A state
  // flag (not a ref) guarantees the write effect can only fire on a render AFTER
  // the read applied, so a deep-linked ?startDate/?endDate is never clobbered by
  // the default range — independent of when the project list resolves.
  const [dateSyncReady, setDateSyncReady] = useState(false);

  useAnalytics({ currentUser, project: gps.selectedProjet?.label, module: 'GPS' });


  // When selected project changes, reflect it in the URL (preserve other params)
  useEffect(() => {
    if (gps.projets.length === 0) return;

    const newSearchParams = new URLSearchParams(searchParams);

    if (gps.selectedProjet?.value) {
      newSearchParams.set('projectId', gps.selectedProjet.value);
    } else {
      newSearchParams.delete('projectId');
    }
    // Only push if changed
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps.selectedProjet, gps.projets]);

  // On load or when projects list is available, preselect project from URL if any
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    const currentId = gps.selectedProjet?.value?.toString();

    if (projectId && projectId !== currentId && (gps.projets || []).length > 0) {
      const match = gps.projets.find(p => String(p.value) === String(projectId));

      if (match) {
        gps.setSelectedProjet(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps.projets]);

  // On load or when projects available, read date range / period from the URL into
  // context once. Runs before the write effect's first real pass (see ref guard).
  useEffect(() => {
    if (dateSyncReady) return;
    if ((gps.projets || []).length === 0) return;

    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlPeriodType = searchParams.get('periodType');

    if (urlStartDate) {
      const parsed = safeParseDate(urlStartDate);
      if (parsed) gps.setStartDate(parsed);
    }
    if (urlEndDate) {
      const parsed = safeParseDate(urlEndDate);
      if (parsed) gps.setEndDate(parsed);
    }
    if (urlPeriodType) {
      gps.setPeriodType(urlPeriodType);
    }
    // A stale/missing periodType is reconciled by DateRangeSelector's authoritative
    // preset-matching (onPeriodTypeResolved) once the range is applied.
    setDateSyncReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps.projets, dateSyncReady]);

  // When the date range / period changes, reflect it in the URL (preserve other params).
  // Gated on dateSyncReady so it only runs after the initial URL read applied —
  // never on the same render, so a deep-linked range survives mount.
  useEffect(() => {
    if (!dateSyncReady) return;

    const newSearchParams = new URLSearchParams(searchParams);

    const startParam = toDateParam(gps.startDate);
    const endParam = toDateParam(gps.endDate);

    if (startParam) {
      newSearchParams.set('startDate', startParam);
    } else {
      newSearchParams.delete('startDate');
    }
    if (endParam) {
      newSearchParams.set('endDate', endParam);
    } else {
      newSearchParams.delete('endDate');
    }
    if (gps.periodType) {
      newSearchParams.set('periodType', gps.periodType);
    } else {
      newSearchParams.delete('periodType');
    }

    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps.startDate, gps.endDate, gps.periodType, dateSyncReady]);

  const buildHref = useCallback((tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === 'general') next.delete('tab'); else next.set('tab', tabId);
    const qs = next.toString();
    return qs ? `?${qs}` : '';
  }, [searchParams]);

  // Custom pages sit right after "Général", in the order the API lists them.
  const pageIcon = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18l-2-5H5L3 9z"/><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"/><path d="M9 20v-6h6v6"/></svg>;
  const extraTabs = (customPages || []).map((page) => ({
    id: page.id,
    label: page.title,
    subItems: subItemsOf(page),
    icon: pageIcon,
  }));
  const generalIdx = tabs.findIndex(t => t.id === 'general');
  const insertAt = generalIdx === -1 ? tabs.length : generalIdx + 1;
  const sidebarTabs = [...tabs.slice(0, insertAt), ...extraTabs, ...tabs.slice(insertAt)];

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-[#0d0d0d]">
      <ViewerSidebar
        header={!jail && (
          <CardSelect
            label="Projet"
            value={gps.selectedProjet}
            onChange={gps.setSelectedProjet}
            options={gps.projets}
            placeholder="Choisir un projet..."
            searchPlaceholder="Rechercher un projet..."
            sectionLabel="Vos projets"
          />
        )}
        showNav={!!gps.selectedProjet}
        tabs={sidebarTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        buildHref={buildHref}
        navLabel="GPS Navigation"
        activeSubItem={activeSub}
        onSubItemChange={setActiveSub}
        buildSubHref={buildSubHref}
      />

      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0d0d0d]">
        <div className="container mx-auto p-4 text-left">
          {gps.selectedProjet ? (
            <>
              {activeTab !== 'lexicon' && <GPSSelectionHeader jail={jail} activeTab={activeTab} />}
              <div className={activeTab !== 'lexicon' ? 'mt-4' : ''}>
                {isCustomPage ? (
                  activePage && <GPSCustomPage page={activePage} activeTabId={activeSub} />
                ) : (
                  <GPSTabs active={activeTab} currentUser={currentUser} />
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-center bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-900 rounded-xl p-10 mt-2">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-3H5a2 2 0 0 0-2 2z" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Aucun projet sélectionné</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Sélectionnez un projet dans le menu à gauche pour visualiser les statistiques GPS.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GPSViewer({ currentUser }) {
  return (
    <GPSProvider>
      <GPSViewerContent currentUser={currentUser} />
    </GPSProvider>
  );
}

export default GPSViewer;
